import React, { useEffect, useMemo, useState } from 'react';

import { SummaryType } from '@ariksa/inventory-core/api';
import { VulnerabilitiesCharts } from '@ariksa/reporting/api';
import { Resources } from '@ariksa/reporting/dist/api';
import { Center } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { usePageContentContext } from 'components/Layout';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { selectApp } from 'containers/App/selectors';
import { NoAccountsOnboarded } from 'containers/Dashboard/Overview/Components/NoAccountsOnboarded';
import { Vulnerabilities } from 'containers/Dashboard/VulnerabilityOverview/Components';
import { selectSharedState } from 'containers/SharedState/selectors';
import { useInjector } from 'utils/inject';

import { vulnerabilityOverviewSaga } from './saga';
import { reducer, sliceKey, actions } from './slice';
import { getActiveScannerCount } from './utils';

interface Props {
  useScanVaultApis?: boolean;
}

export const VulnerabilityOverview: React.FC<Props> = ({
  useScanVaultApis = false,
}) => {
  useInjector(sliceKey, reducer, vulnerabilityOverviewSaga);
  const dispatch = useDispatch();
  const { cloudAccounts, environments } = useSelector(selectApp);
  const { environmentId, accountId } = useAccessBoundary();
  const {
    aiParamExtraction,
    vulnerabilityScannerInfo,
    centralScanners,
    isVulnerabilityScannerDeployed,
  } = useSelector(selectSharedState);
  const { contentHeight } = usePageContentContext();
  const { environmentMapping } = useEnvironmentOptions();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showVulnerabilities, setShowVulnerabilities] = useState(true);

  // Scanner count was held in state that was never set (always "0 Outposts").
  // Derive it from active scanners for the selected account or environment,
  // plus central scanners, which also count as outposts.
  const vulnerabilityScannerCount = useMemo(() => {
    const accountIds = !!accountId
      ? [String(accountId)]
      : Array.from(environmentMapping?.[environmentId!]?.account_ids ?? []).map(
          String,
        );
    return (
      getActiveScannerCount(vulnerabilityScannerInfo.data, accountIds) +
      (centralScanners.data?.length ?? 0)
    );
  }, [
    accountId,
    environmentId,
    environmentMapping,
    vulnerabilityScannerInfo.data,
    centralScanners.data,
  ]);

  useEffect(() => {
    setEndDate(
      aiParamExtraction.data?.end_date
        ? dayjs(aiParamExtraction.data?.end_date).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD'),
    );

    setStartDate(
      aiParamExtraction.data?.start_date
        ? dayjs(aiParamExtraction.data?.start_date).format('YYYY-MM-DD')
        : dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
    );
  }, [aiParamExtraction.data]);

  /*vulnerabilities*/
  useEffect(() => {
    if ((!environmentId && !accountId) || !startDate || !endDate) return;

    if (useScanVaultApis) {
      setShowVulnerabilities(true);
      return;
    }

    if (!isEmpty(centralScanners.data)) {
      setShowVulnerabilities(true);
    } else {
      // Removed a repeated `if (!data) return` that could never fire inside this check.
      if (!!vulnerabilityScannerInfo.data) {
        const accounts = environmentMapping?.[environmentId!]?.account_ids;

        if (!!accountId)
          setShowVulnerabilities(
            !!isVulnerabilityScannerDeployed[String(accountId)],
          );
        else
          setShowVulnerabilities(
            !!(
              environmentId && isVulnerabilityScannerDeployed[environmentId]
            ) ||
              Array.from(accounts ?? []).some(accountId =>
                Boolean(isVulnerabilityScannerDeployed[String(accountId)]),
              ),
          );
      }
    }
  }, [
    environmentId,
    accountId,
    useScanVaultApis,
    aiParamExtraction.data,
    environmentMapping,
    endDate,
    startDate,
    vulnerabilityScannerInfo.data,
    isVulnerabilityScannerDeployed,
    centralScanners.data,
  ]);

  /*vulnerabilities*/
  useEffect(() => {
    if ((!environmentId && !accountId) || !startDate || !endDate) return;
    const accountIds = !!accountId ? [accountId] : undefined;
    const startDate_7 = aiParamExtraction.data?.start_date
      ? dayjs(aiParamExtraction.data?.start_date).format('YYYY-MM-DD')
      : dayjs().subtract(7, 'day').format('YYYY-MM-DD');

    /*if (
      (vulnerabilityScannerInfo.isSuccess &&
        !vulnerabilityScannerInfo.isLoading) ||
      (centralScanners.isSuccess && !centralScanners.isLoading)
    ) {
      if (!vulnerabilityScannerInfo.data && isEmpty(centralScanners.data))
        return;*/

    if (showVulnerabilities) {
      dispatch(
        actions.getTopNewVulnerabilities({
          q: {
            startDate: startDate,
            endDate: endDate,
            environmentId,
            accountId: accountIds,
          },
        }),
      );
      dispatch(
        actions.getMostWidelyDeployedLibraries({
          q: {
            environmentId,
            accountId: accountIds,
          },
        }),
      );
      dispatch(
        actions.getHighestOccurringVulnerabilities({
          q: {
            environmentId,
            accountId: accountIds,
          },
        }),
      );
      dispatch(
        actions.getVulnerabilitiesByResourceTypes({
          q: {
            environmentId,
            accountId: accountIds,
            exploitable: true,
          },
        }),
      );
      dispatch(
        actions.getExploitableEntitiesToPatch({
          q: {
            environmentId: environmentId as string,
            accountId: accountIds,
            startDate,
            endDate,
            chartType: VulnerabilitiesCharts.ExploitableEntitiesToPatch,
          },
        }),
      );
      dispatch(
        actions.getVulnerableEntitiesWithSensitiveDataAccess({
          q: {
            environmentId: environmentId as string,
            accountId: accountIds,
            summaryType: SummaryType.VulnerableWithPrivilegedAccessToSensitive,
          },
        }),
      );
      dispatch(
        actions.getCriticalAndHighVulnerabilitiesSummary({
          q: {
            startDate,
            endDate,
            environmentId,
            accountId: accountIds,
          },
        }),
      );
      dispatch(
        actions.getEOLSoftware({
          q: {
            environmentId,
            accountId: accountIds,
          },
        }),
      );
      dispatch(
        actions.getServerlessVulnerabilitiesCount({
          q: {
            environmentId: environmentId as string,
            accountId: accountIds,
            agnosticResource: Resources.ServerlessCompute,
            endDate,
            startDate: startDate_7,
          },
        }),
      );
      dispatch(
        actions.getVulnerabilitiesCount({
          q: {
            environmentId: environmentId as string,
            accountId,
            startDate: startDate_7,
            endDate,
          },
        }),
      );
      dispatch(
        actions.getVmVulnerabilitiesCount({
          q: {
            environmentId: environmentId as string,
            accountId: accountIds,
            agnosticResource: Resources.VirtualMachine,
            endDate,
            startDate: startDate_7,
          },
        }),
      );
      dispatch(
        actions.getContainerVulnerabilitiesCount({
          q: {
            environmentId: environmentId as string,
            accountId: accountIds,
            agnosticResource: Resources.Containers,
            endDate,
            startDate: startDate_7,
          },
        }),
      );
      dispatch(
        actions.getContainerServiceVulnerabilitiesCount({
          q: {
            environmentId: environmentId as string,
            accountId: accountIds,
            agnosticResource: Resources.ContainerRegistry,
            endDate,
            startDate: startDate_7,
          },
        }),
      );
      dispatch(
        actions.getVmImageVulnerabilitiesCount({
          q: {
            environmentId: environmentId as string,
            accountId: accountIds,
            agnosticResource: Resources.Image,
            endDate,
            startDate: startDate_7,
          },
        }),
      );
      dispatch(
        actions.getVulnerabilityPrioritizationData({
          q: {
            environmentId: environmentId as string,
            accountId: accountIds,
            useScanVaultVulnerabilityFindings: useScanVaultApis,
          },
        }),
      );
      //}
    }
  }, [
    dispatch,
    environmentId,
    accountId,
    showVulnerabilities,
    useScanVaultApis,
    aiParamExtraction.data,
    environmentMapping,
    endDate,
    startDate,
    vulnerabilityScannerInfo.isLoading,
    vulnerabilityScannerInfo.isSuccess,
    vulnerabilityScannerInfo.data,
    centralScanners.isLoading,
    centralScanners.isSuccess,
  ]);

  return (
    <>
      {environments.isLoading || cloudAccounts.loading ? (
        <Center h={contentHeight + 'px'}>
          <CustomSpinner size="xl" />
        </Center>
      ) : (
        <>
          {!isEmpty(cloudAccounts.data) && cloudAccounts.loaded && (
            <Vulnerabilities
              showDashboard={showVulnerabilities}
              vulnerabilityScannerCount={vulnerabilityScannerCount}
              useScanVaultApis={useScanVaultApis}
            />
          )}
          {isEmpty(cloudAccounts.data) && cloudAccounts.loaded && (
            <NoAccountsOnboarded />
          )}
        </>
      )}
    </>
  );
};
